import React from "react";
import { Formik } from "formik";
import humanize from "underscore.string/humanize";
// import Dropzone from "react-dropzone";
import { otherIncomeTypes } from "web_modules/underwriter_dashboard/shared";
import { LoanApplicationBorrowers } from "@eave/shared/types/api";

import {
  Button,
  ButtonSet,
  Checkbox,
  InlineNotification,
  Form,
  FormGroup,
  MultiSelect,
  Select,
  SelectItem,
  TextInput,
} from "carbon-components-react";
import { EditingState } from "web_modules/shared/BaseTable";
import { makeEvent } from "web_modules/shared/FormHelpers/carbon-helpers";
import { AdditionalIncomeForm } from "../income_tables/OtherIncomeTable";
import { getBorrowerEntityAsString } from "@eave/shared/utils/borrowers";
import { EMDASH } from "underwriter_dashboard/hurdle_editors/AssetSummary/shared";

const { Creatable } = require("react-select");

interface Props {
  otherIncomeDocs: any;
  formState: AdditionalIncomeForm;
  onSubmit: any;
  borrowers: LoanApplicationBorrowers;
  editingMode: EditingState;
  onRequestClose: () => any;
}

const OtherIncomeModalForm: React.FC<Props> = ({
  otherIncomeDocs,
  formState,
  onSubmit,
  borrowers,
  editingMode,
  onRequestClose,
}: Props) => {
  const incomeTypeOptions = otherIncomeTypes.map(option => ({ label: option, value: option }));

  const hasOtherIncomeDocs = otherIncomeDocs.length > 0;

  const otherIncomeDocsOptions = otherIncomeDocs.map(doc => {
    return {
      text: `${humanize(doc.type)} (${doc.revised_filename || doc.original_filename})`,
      value: doc.id,
    };
  });

  return (
    <Formik enableReinitialize onSubmit={onSubmit} initialValues={formState}>
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => {
        const {
          associated_document_ids,
          description,
          borrower_entity,
          include_in_ratios,
          notes,
          operator_income_type,
        } = values;

        const { previous_associated_document_ids } = formState;
        const initiallySelectedItems = otherIncomeDocsOptions.filter(doc =>
          previous_associated_document_ids.includes(doc.value),
        );

        return (
          <Form
            onSubmit={e => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <FormGroup legendText="">
              <TextInput
                id="description"
                size="sm"
                value={description}
                labelText="Disclosed Description"
                type="text"
                disabled={true}
              />
              <div style={{ marginTop: "10px" }} />
              <label className="bx--label">Actual Description</label>
              <Creatable
                id="operator_income_type"
                name="operator_income_type"
                className="select_additional_income_type"
                placeholder="Income type"
                options={incomeTypeOptions}
                value={
                  operator_income_type in incomeTypeOptions
                    ? operator_income_type
                    : {
                        label: operator_income_type,
                        value: operator_income_type,
                      }
                }
                required={!description}
                onChange={e => {
                  const event = makeEvent("operator_income_type", e.value);
                  handleChange(event);
                }}
              />
              <div style={{ marginBottom: "10px" }} />
              <InlineNotification
                hideCloseButton
                lowContrast
                kind={hasOtherIncomeDocs ? "info" : "warning"}
                notificationType="inline"
                role="alert"
                title={hasOtherIncomeDocs ? `Note: ` : `Warning: `}
                subtitle="To associate this income with a document, upload or update an existing document with the type 'Income Other' and fill out the 'Document description' and 'Monthly amount'."
              />
              <MultiSelect
                id="associated-documents-multi-select"
                key={`${initiallySelectedItems}`}
                name="asssociated_document_ids"
                label='Associated "Income Other" Documents'
                itemToString={item => (item ? item.text : EMDASH)}
                items={otherIncomeDocsOptions}
                value={associated_document_ids}
                initialSelectedItems={initiallySelectedItems}
                onChange={e => {
                  const event = makeEvent(
                    "associated_document_ids",
                    e.selectedItems.map(item => item.value),
                  );
                  handleChange(event);
                }}
                disabled={otherIncomeDocsOptions.length === 0}
                size="sm"
              />
            </FormGroup>
            <FormGroup legendText="">
              {/* We may want to enable document uploading later, so keep for now */}
              {/* <Dropzone
                onDrop={files => {
                  const event = makeEvent("files", files);
                  handleChange(event);
                }}
                className="dropzone"
                activeClassName="active"
                multiple={false}
              >
                {files && files.length > 0
                  ? `${files.map(f => f.name).join(",")} uploaded`
                  : "Upload document..."}
              </Dropzone> */}
              <Select
                id="borrower-name"
                name="borrower_entity"
                value={borrower_entity}
                invalid={!!errors.borrower_entity && touched.borrower_entity}
                invalidText={errors.borrower_entity}
                labelText="Borrower"
                onBlur={handleBlur}
                required={true}
                onChange={e => {
                  const event = makeEvent("borrower_entity", parseInt(e.target.value, 10));
                  handleChange(event);
                }}
                size="sm"
              >
                <SelectItem text="Select a borrower" value={0} />
                {borrowers &&
                  Object.keys(borrowers).map(be => (
                    <SelectItem
                      key={be}
                      value={Number(be)}
                      text={getBorrowerEntityAsString(Number(be))}
                    />
                  ))}
              </Select>
              <div style={{ marginBottom: "10px" }} />
              <TextInput
                id="other-income-notes"
                name="notes"
                size="sm"
                value={notes}
                labelText="Notes (optional)"
                placeholder="Enter a note..."
                type="text"
                invalid={!!errors.notes && touched.notes}
                invalidText={errors.notes}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <div style={{ marginBottom: "10px" }} />
            </FormGroup>
            <FormGroup legendText="Include income in calculations">
              <Checkbox
                id="include_income_ratios"
                name="include_income_ratios"
                labelText="Include income in ratios?"
                checked={include_in_ratios}
                onChange={() => {
                  const e = makeEvent("include_in_ratios", !include_in_ratios);
                  handleChange(e);
                }}
              />
            </FormGroup>
            <ButtonSet>
              <Button
                kind="secondary"
                size="lg"
                onClick={() => onRequestClose()}
                style={{ maxWidth: "50%" }}
              >
                Cancel
              </Button>
              <Button kind="primary" size="lg" type="submit" style={{ maxWidth: "50%" }}>
                {editingMode === EditingState.CREATE ? "Save" : "Update"}
              </Button>
            </ButtonSet>
          </Form>
        );
      }}
    </Formik>
  );
};

export default OtherIncomeModalForm;
